import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHeart, faShieldAlt, faUsers, faStar, faBullseye, faEye, faGem } from '@fortawesome/free-solid-svg-icons';
import { motion } from 'framer-motion';
import VisibilitySensor from 'react-visibility-sensor';

// Shared data
const tabContent = {
  mission: {
    title: 'Our Mission',
    body: 'Empower healthcare providers with software that streamlines operations and enhances patient care.',
    icon: faBullseye,
  },
  vision: {
    title: 'Our Vision',
    body: 'Enable Pakistan\'s healthcare sector with practical, user-friendly digital systems.',
    icon: faEye,
  },
};

const companyIntro = {
  title: 'Who We Are',
  description: (
    <>
      <b>D-Right Technologies (Pvt) Ltd</b> is a healthcare technology company
      partnering with <b>ClinTa Healthcare (USA)</b> to support Pakistan's
      healthcare sector. Our goal is to help healthcare providers
      manage operations smoothly through practical and user-friendly
      software solutions.
    </>
  ),
};

const coreValuesPara =
  'Innovation, reliability, customer focus, and excellence guide everything we do.';

const values = [
  { icon: faHeart, title: 'Innovation', body: 'Redefining boundaries through cutting-edge solutions.' },
  { icon: faShieldAlt, title: 'Reliability', body: 'Building trust through consistent, dependable performance.' },
  { icon: faUsers, title: 'Customer Focus', body: 'Putting our clients\' needs at the center of everything we do.' },
  { icon: faStar, title: 'Excellence', body: 'Striving for the highest standards in all our endeavors.' },
];

// Standard layout (Cards with tabs)
export function WhoWeAreStandard({ introComponent: IntroComponent }) {
  const [activeTab, setActiveTab] = useState('mission');
  const [isVisible, setIsVisible] = useState(false);
  const DefaultIntro = IntroComponent || (() => (
    <>
      <h2 className="wwa-heading">{companyIntro.title}</h2>
      <p className="mb-0 wwa-intro">{companyIntro.description}</p>
    </>
  ));

  return (
    <VisibilitySensor onChange={(visible) => visible && !isVisible && setIsVisible(true)} partialVisibility>
      <section className="ptb-100 gray-light-bg wwa-section">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-md-10 col-lg-9">
              <div className="section-heading text-center mb-4">
                {IntroComponent ? (
                  <IntroComponent isVisible={isVisible} />
                ) : (
                  <DefaultIntro />
                )}
              </div>
            </div>
          </div>
          <div className="row justify-content-center">
            <div className="col-lg-10">
              <div className="wwa-tabs-wrap bg-white border rounded shadow-sm">
                <div className="row no-gutters">
                  <div className="col-md-4">
                    <div className="wwa-tabs-left p-3 p-md-4">
                      <button
                        type="button"
                        className={`wwa-tab-btn ${activeTab === 'mission' ? 'active' : ''}`}
                        onClick={() => setActiveTab('mission')}
                      >
                        <span className="wwa-tab-icon" aria-hidden="true">🎯</span>
                        <span>Our Mission</span>
                      </button>
                      <button
                        type="button"
                        className={`wwa-tab-btn ${activeTab === 'vision' ? 'active' : ''}`}
                        onClick={() => setActiveTab('vision')}
                      >
                        <span className="wwa-tab-icon" aria-hidden="true">👁️</span>
                        <span>Our Vision</span>
                      </button>
                    </div>
                  </div>
                  <div className="col-md-8">
                    <div className="wwa-tabs-right p-4 p-md-5">
                      <h4 className="mb-2 d-none d-md-block">{tabContent[activeTab].title}</h4>
                      <p className="mb-0" style={{ color: '#696969' }}>{tabContent[activeTab].body}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="row justify-content-center mt-4">
            <div className="col-lg-10">
              <div className="text-center mb-4">
                <h3 className="mb-2">Our Core Values</h3>
                <p className="mb-0" style={{ color: '#696969' }}>
                  These fundamental principles guide everything we do and shape our commitment to excellence.
                </p>
              </div>
              <div className="wwa-values-grid">
                {values.map((v) => (
                  <div className="wwa-value" key={v.title}>
                    <div className="wwa-value-icon" aria-hidden="true">
                      <FontAwesomeIcon icon={v.icon} />
                    </div>
                    <div className="wwa-value-title">{v.title}</div>
                    <div className="wwa-value-body">{v.body}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </VisibilitySensor>
  );
}

// Side-by-Side Gradient layout
export function WhoWeAreSideBySideGradient({ introComponent: IntroComponent }) {
  const [isVisible, setIsVisible] = useState(false);

  return (
    <VisibilitySensor onChange={(visible) => visible && !isVisible && setIsVisible(true)} partialVisibility>
      <section className="ptb-100 gray-light-bg wwa-section">
        <div className="container">
          <motion.div
            className="row justify-content-center mb-3"
            initial={{ opacity: 0 }}
            animate={isVisible ? { opacity: 1 } : { opacity: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="col-md-10 col-lg-10 text-center mb-0">
              {IntroComponent ? (
                <IntroComponent isVisible={isVisible} />
              ) : (
                <div className="section-heading">
                  <h2 className="web mb-3">{companyIntro.title}</h2>
                  <p className="wwa-intro">{companyIntro.description}</p>
                </div>
              )}
            </div>
          </motion.div>

          <div className="row g-3 wwa-mission-vision-values-cards">
            {Object.entries(tabContent).map(([key, content], idx) => (
              <motion.div
                key={key}
                className="col-12 col-md-4"
                initial={{ opacity: 0, y: 50 }}
                animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
                transition={{ duration: 0.5, delay: idx * 0.2 }}
              >
                <motion.div
                  className={`wwa-mvv-card wwa-mvv-card-${idx + 1}`}
                  whileHover={{ y: -4 }}
                >
                  <div className="wwa-mvv-header-row">
                    <div className="wwa-mvv-icon-wrap">
                      <FontAwesomeIcon icon={content.icon} className="wwa-mvv-icon" />
                    </div>
                    <h3 className="wwa-mvv-title">{content.title}</h3>
                  </div>
                  <p className="wwa-mvv-body">{content.body}</p>
                </motion.div>
              </motion.div>
            ))}
            <motion.div
              className="col-12 col-md-4"
              initial={{ opacity: 0, y: 50 }}
              animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              <motion.div
                className="wwa-mvv-card wwa-mvv-card-3"
                whileHover={{ y: -4 }}
              >
                <div className="wwa-mvv-header-row">
                  <div className="wwa-mvv-icon-wrap">
                    <FontAwesomeIcon icon={faGem} className="wwa-mvv-icon" />
                  </div>
                  <h3 className="wwa-mvv-title">Our Core Values</h3>
                </div>
                <p className="wwa-mvv-body">{coreValuesPara}</p>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>
    </VisibilitySensor>
  );
}
